import { useEffect, useRef, useState } from 'react';
import { Avatar } from './shared/Avatar';
import { getAvatar, saveAvatar } from '../utils/avatar-store';
import { useT } from '../utils/i18n';
import styles from './AgentDetailModal.module.css';

const PRESET_COLORS = [
  '#e34a2a', '#5a8ab8', '#c79a3a', '#5a6a2a', '#8a6ac8',
  '#7ec9a8', '#d47b9e', '#e8a04c', '#4a7a6a', '#6a5a8a',
  '#b85a5a', '#5a9a5a',
];

// Cache agent soul data in memory to avoid re-fetching on every modal open
const soulCache = new Map<string, string>();

interface AgentDetailModalProps {
  botName: string;
  description?: string;
  onClose: () => void;
  onDeleted?: () => void;
}

export function AgentDetailModal({ botName, description, onClose, onDeleted }: AgentDetailModalProps) {
  const t = useT();
  const [soul, setSoul] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const saved = getAvatar(botName);
  const [imageUrl, setImageUrl] = useState<string | null>(saved.imageUrl || null);
  const [presetColor, setPresetColor] = useState<string | null>(saved.presetColor || null);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const token = localStorage.getItem('novax:token') || '';

  useEffect(() => {
    if (soulCache.has(botName)) {
      const cached = soulCache.get(botName)!;
      setSoul(cached);
      setDraft(cached);
      setLoading(false);
      return;
    }
    fetch(`/api/agent-soul/${encodeURIComponent(botName)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        const val = d.soul || '';
        setSoul(val);
        setDraft(val);
        soulCache.set(botName, val);
      })
      .catch(() => setSoul(''))
      .finally(() => setLoading(false));
  }, [botName, token]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showAvatarPicker) setShowAvatarPicker(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, showAvatarPicker]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`/api/agent-soul/${encodeURIComponent(botName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ soul: draft }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.saved) {
        setSoul(draft);
        soulCache.set(botName, draft);
        setEditing(false);
      }
    } catch (err) {
      console.error('Failed to save soul:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    setDraft(soul);
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleCancel = () => {
    setDraft(soul);
    setEditing(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`/api/bots/${encodeURIComponent(botName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        alert(data.error || 'Failed to delete');
        return;
      }
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete bot:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      setImageUrl(url);
      setPresetColor(null);
      setShowAvatarPicker(false);
      saveAvatar(botName, { imageUrl: url });
    };
    reader.readAsDataURL(file);
  };

  const handlePresetSelect = (color: string) => {
    setPresetColor(color);
    setImageUrl(null);
    setShowAvatarPicker(false);
    saveAvatar(botName, { presetColor: color });
  };

  const initial = botName.charAt(0).toUpperCase();

  const renderAvatar = (size: 'xxl' | 'lg') => {
    if (imageUrl) return <Avatar name={botName} size={size} imageUrl={imageUrl} />;
    if (presetColor) {
      const px = size === 'xxl' ? 80 : 52;
      return (
        <div
          style={{
            width: px, height: px, borderRadius: '50%',
            border: '1.5px solid #1b1b14', backgroundColor: presetColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: px * 0.42, color: '#1b1b14',
          }}
        >
          {initial}
        </div>
      );
    }
    return <Avatar name={botName} size={size} />;
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>x</button>

        <div className={styles.header}>
          {/* Avatar with pencil hover overlay */}
          <div className={styles.avatarWrap} onClick={() => setShowAvatarPicker(true)}>
            {renderAvatar('xxl')}
            <div className={styles.avatarOverlay}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                <path d="m15 5 4 4"/>
              </svg>
            </div>
          </div>

          <div>
            <h2 className={styles.name}>{botName}</h2>
            {description && <p className={styles.desc}>{description}</p>}
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.soulSection}>
          <div className={styles.soulHeader}>
            <h3 className={styles.soulLabel}>{t('detail.soul')}</h3>
            {!editing ? (
              <button className={styles.editBtn} onClick={handleEdit}>{t('detail.edit')}</button>
            ) : (
              <div className={styles.editActions}>
                <button className={styles.cancelBtn} onClick={handleCancel}>{t('detail.cancel')}</button>
                <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? t('detail.saving') : t('detail.save')}
                </button>
              </div>
            )}
          </div>
          {loading ? (
            <div className={styles.soulContent}>Loading...</div>
          ) : editing ? (
            <textarea
              ref={textareaRef}
              className={styles.soulEditor}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
            />
          ) : soul ? (
            <pre className={styles.soulContent}>{soul}</pre>
          ) : (
            <div className={`${styles.soulContent} ${styles.soulEmpty}`}>
              {t('detail.noSoul')}
            </div>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.dangerZone}>
          {!confirmDelete ? (
            <button className={styles.deleteBtn} onClick={() => setConfirmDelete(true)}>
              {t('detail.deleteAgent')}
            </button>
          ) : (
            <div className={styles.confirmRow}>
              <span className={styles.confirmText}>{t('detail.confirmDelete')}</span>
              <button className={styles.confirmCancel} onClick={() => setConfirmDelete(false)}>{t('detail.cancel')}</button>
              <button className={styles.confirmDelete} onClick={handleDelete} disabled={deleting}>
                {deleting ? t('detail.deleting') : t('detail.yesDelete')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Avatar picker popup */}
      {showAvatarPicker && (
        <div className={styles.pickerOverlay} onClick={() => setShowAvatarPicker(false)}>
          <div className={styles.pickerModal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.pickerTitle}>{t('detail.changeAvatar')}</h3>

            <button className={styles.uploadBtn} onClick={() => fileRef.current?.click()}>
              {t('detail.uploadImage')}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={handleUpload}
            />

            <div className={styles.pickerDivider} />
            <span className={styles.pickerLabel}>{t('detail.choosePreset')}</span>

            <div className={styles.presetGrid}>
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  className={`${styles.presetOption} ${presetColor === c ? styles.presetOptionActive : ''}`}
                  style={{ backgroundColor: c }}
                  onClick={() => handlePresetSelect(c)}
                >
                  {initial}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
